import { getAuthToken, getSessionId } from './user';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import { detectExperiment } from './googleOptimize';
import { fetchUserVariantsBySessionId } from '@/redux/api/userVariantTest';
import { getDeployment } from './config';
import { GlobalState } from '../rootReducer';
import ms from 'ms';

/* Action Types */
export const LOAD_USER_VARIANTS_FAIL = 'la/domain/userVariants/LOAD_FAIL';
export const LOAD_USER_VARIANTS_REQUEST = 'la/domain/userVariants/LOAD_REQUEST';
export const LOAD_USER_VARIANTS_SUCCESS = 'la/domain/userVariants/LOAD_SUCCESS';

const REDUX_STORE_TIME = ms('20m');

/* reducer */
const DEFAULT_STATE = {
    byId: {},
    loaded: 0,
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let existing;
    let loaded;

    switch (action.type) {
        case LOAD_USER_VARIANTS_FAIL:
            return state;
        case LOAD_USER_VARIANTS_REQUEST:
            return state;
        case LOAD_USER_VARIANTS_SUCCESS:
            existing = cloneDeep(state.byId);
            loaded = state.loaded;

            if (action.payload) {
                action.payload.forEach((item) => {
                    existing[item.experimentId] = { ...item };
                });
                loaded = action.meta.actionTime;
            }

            return {
                ...state,
                byId: existing,
                loaded,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.userVariantTest;
const idSelector = (state: GlobalState, id) => id;

const byIdSelector = createSelector(stateSelector, (state) => state.byId);

const loadedSelector = createSelector(stateSelector, (state) => state.loaded);

const getLoadedTs = createSelector(loadedSelector, (loaded) => loaded);

export const getUserExperimentVariant: (arg0: any, arg1: string) => any = createSelector(
    [byIdSelector, idSelector],
    (byId, id) => byId[id] || {}
);

/* ACTION CREATORS */
const fetchIfNeeded = async ({ dispatch, getState }) => {
    try {
        const state = getState();
        const loadedTs = getLoadedTs(state) + REDUX_STORE_TIME;
        const timeoutTs = Date.now();
        if (loadedTs >= timeoutTs) {
            return;
        }
        const uuidRegExp = /\b[0-9a-f]{8}\b-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-\b[0-9a-f]{12}\b/;
        const userSessionId = getSessionId();
        if (String(userSessionId).match(uuidRegExp)) {
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);
            dispatch({
                type: LOAD_USER_VARIANTS_REQUEST,
            });
            const response = await fetchUserVariantsBySessionId({
                authToken,
                deployment,
                userSessionId,
            });
            dispatch({
                meta: { actionTime: Date.now() },
                payload: response.payload,
                type: LOAD_USER_VARIANTS_SUCCESS,
            });
            dispatch(detectExperiment());
        }
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: LOAD_USER_VARIANTS_FAIL,
        });
    }
};

export const fetchUserVariantsIfNeeded = () => async (dispatch: Function, getState: Function) => {
    return fetchIfNeeded({ dispatch, getState });
};
